// Onboarding admin: create prospect invites, copy/send links, track status, view submissions.
import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';
const NAVY = '#12234f', BORDER = '#d5dcea', INK = '#1c2333', MUTED = '#78839a';

const chip = (status) => {
  const map = {
    sent: ['#eef2fa', '#3a4a6b', 'Sent'],
    opened: ['#fff7e0', '#8a6d1a', 'Opened'],
    submitted: ['#e6f4ea', '#1a7f37', 'Submitted'],
    provisioned: ['#e8e0f7', '#5b3fa8', 'Provisioned'],
    revoked: ['#fdecea', '#b03030', 'Revoked'],
  };
  const [bg, fg, label] = map[status] || ['#eee', '#555', status];
  return <span style={{ background: bg, color: fg, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>{label}</span>;
};

const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 14,
  border: '1px solid ' + BORDER, borderRadius: 8, outline: 'none', color: INK };

export default function Onboarding({ user }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ prospect_name: '', company_name: '', prospect_email: '', prospect_phone: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [detail, setDetail] = useState(null);
  const [copied, setCopied] = useState('');

  const hdrs = { Authorization: 'Bearer ' + user.token, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    fetch(API + '/api/onboarding/invites', { headers: hdrs })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Load failed'); return d; })
      .then(d => { setInvites(d); setErr(''); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.token]);

  useEffect(() => { load(); }, [load]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(id); setTimeout(() => setCopied(''), 1600); });
  };

  const create = () => {
    if (!form.prospect_name.trim()) { alert('Prospect name is required'); return; }
    setCreating(true);
    fetch(API + '/api/onboarding/invites', { method: 'POST', headers: hdrs, body: JSON.stringify(form) })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Create failed'); return d; })
      .then(d => { setCreated(d); setForm({ prospect_name: '', company_name: '', prospect_email: '', prospect_phone: '', notes: '' }); load(); })
      .catch(e => alert(e.message))
      .finally(() => setCreating(false));
  };

  const revoke = (id) => {
    if (!window.confirm('Deactivate this link? The prospect will no longer be able to open it.')) return;
    fetch(API + '/api/onboarding/invites/' + id + '/revoke', { method: 'POST', headers: hdrs })
      .then(r => r.json()).then(() => load());
  };

  const openDetail = (id) => {
    fetch(API + '/api/onboarding/invites/' + id, { headers: hdrs })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Load failed'); return d; })
      .then(setDetail)
      .catch(e => alert(e.message));
  };

  const mailtoFor = (inv) => {
    const subject = encodeURIComponent('Your TCIO onboarding link' + (inv.company_name ? ' - ' + inv.company_name : ''));
    const body = encodeURIComponent(
      'Hi ' + (inv.prospect_name || '').split(' ')[0] + ',\n\n' +
      'Here is your TCIO onboarding link:\n\n' + inv.link + '\n\n' +
      'It works on your phone or computer, saves automatically as you type, and you can return to it anytime with the same link. ' +
      'Please do not enter any client/patient information in the form.\n\n' +
      'Thank you,\nTCIO - Treatment Centers Intelligence OS');
    return 'mailto:' + (inv.prospect_email || '') + '?subject=' + subject + '&body=' + body;
  };

  if (detail) return <DetailView detail={detail} onBack={() => { setDetail(null); load(); }} />;

  return (
    <div style={{ padding: '18px 22px', maxWidth: 1100, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, color: NAVY }}>Onboarding</h2>
          <div style={{ fontSize: 13, color: MUTED }}>Send prospects a no-login questionnaire link. Their answers become their facility setup.</div>
        </div>
        <button onClick={() => { setShowNew(s => !s); setCreated(null); }}
          style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          + New Invite
        </button>
      </div>

      {showNew && (
        <div style={{ border: '1px solid ' + BORDER, borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafbfe' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label><div style={lbl}>Prospect name *</div>
              <input style={inp} value={form.prospect_name} onChange={e => setForm(f => ({ ...f, prospect_name: e.target.value }))} /></label>
            <label><div style={lbl}>Company / facility</div>
              <input style={inp} value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} /></label>
            <label><div style={lbl}>Email</div>
              <input style={inp} value={form.prospect_email} onChange={e => setForm(f => ({ ...f, prospect_email: e.target.value }))} /></label>
            <label><div style={lbl}>Phone</div>
              <input style={inp} value={form.prospect_phone} onChange={e => setForm(f => ({ ...f, prospect_phone: e.target.value }))} /></label>
          </div>
          <label><div style={lbl}>Notes (internal)</div>
            <input style={{ ...inp, marginBottom: 10 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></label>
          <button disabled={creating} onClick={create}
            style={{ background: '#1a7f37', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {creating ? 'Creating...' : 'Create invite link'}
          </button>
          {created && (
            <div style={{ marginTop: 12, padding: 12, background: '#e6f4ea', borderRadius: 10, border: '1px solid #b7dfc2' }}>
              <div style={{ fontWeight: 700, color: '#1a7f37', marginBottom: 6 }}>Invite created for {created.prospect_name}</div>
              <div style={{ fontSize: 13, wordBreak: 'break-all', marginBottom: 8 }}>{created.link}</div>
              <button onClick={() => copy(created.link, 'new')} style={btnSm}>{copied === 'new' ? 'Copied!' : 'Copy link'}</button>
              {form.prospect_email === '' && created.prospect_email ? null : null}
              <a href={mailtoFor(created)} style={{ ...btnSm, textDecoration: 'none', display: 'inline-block', marginLeft: 8 }}>Email it</a>
              <span style={{ fontSize: 12, color: MUTED, marginLeft: 10 }}>or text it to them - it works great on a phone</span>
            </div>
          )}
        </div>
      )}

      {err && <div style={{ color: '#b03030', marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={{ color: MUTED }}>Loading...</div> : (
        <div style={{ border: '1px solid ' + BORDER, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: NAVY, color: '#fff', textAlign: 'left' }}>
                {['Prospect', 'Company', 'Status', 'Last activity', 'Expires', 'Actions'].map(h =>
                  <th key={h} style={{ padding: '10px 12px', fontWeight: 700 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 18, color: MUTED }}>No invites yet. Create one to get started.</td></tr>
              )}
              {invites.map((inv, i) => (
                <tr key={inv.id} style={{ background: i % 2 ? '#fafbfe' : '#fff', borderTop: '1px solid ' + BORDER }}>
                  <td style={{ padding: '9px 12px', fontWeight: 600 }}>{inv.prospect_name}</td>
                  <td style={{ padding: '9px 12px' }}>{inv.company_name || '-'}</td>
                  <td style={{ padding: '9px 12px' }}>{chip(inv.submitted_at ? 'submitted' : inv.status)}</td>
                  <td style={{ padding: '9px 12px', color: MUTED }}>{inv.last_activity ? new Date(inv.last_activity).toLocaleString() : '-'}</td>
                  <td style={{ padding: '9px 12px', color: MUTED }}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => copy(inv.link, inv.id)} style={btnSm}>{copied === inv.id ? 'Copied!' : 'Copy link'}</button>
                    {inv.submitted_at && <button onClick={() => openDetail(inv.id)} style={{ ...btnSm, marginLeft: 6, background: NAVY, color: '#fff', border: '1px solid ' + NAVY }}>View answers</button>}
                    {!inv.submitted_at && inv.status !== 'revoked' &&
                      <button onClick={() => revoke(inv.id)} style={{ ...btnSm, marginLeft: 6, color: '#b03030' }}>Revoke</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 700, color: '#3a4a6b', margin: '6px 0 3px' };
const btnSm = { background: '#fff', color: INK, border: '1px solid ' + BORDER, borderRadius: 8,
  padding: '6px 11px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };

function DetailView({ detail, onBack }) {
  const answers = detail.answers || {};
  const keys = Object.keys(answers).sort();
  const pretty = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const val = (v) => v === true ? 'Yes' : v === false ? 'No' : String(v);
  return (
    <div style={{ padding: '18px 22px', maxWidth: 900 }}>
      <button onClick={onBack} style={{ ...btnSm, marginBottom: 12 }}>&#8592; Back to invites</button>
      <h2 style={{ margin: '0 0 2px', color: NAVY }}>{detail.company_name || detail.prospect_name}</h2>
      <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>
        {detail.prospect_name} {detail.prospect_email ? ' - ' + detail.prospect_email : ''} {detail.prospect_phone ? ' - ' + detail.prospect_phone : ''}
      </div>
      <div style={{ fontSize: 13, color: MUTED, marginBottom: 14 }}>
        Submitted: {detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : 'not yet'} - Answered fields: {keys.length}
      </div>
      <div style={{ border: '1px solid ' + BORDER, borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <tbody>
            {keys.length === 0 && <tr><td style={{ padding: 16, color: MUTED }}>No answers recorded.</td></tr>}
            {keys.map((k, i) => (
              <tr key={k} style={{ background: i % 2 ? '#fafbfe' : '#fff', borderTop: i ? '1px solid ' + BORDER : 'none' }}>
                <td style={{ padding: '8px 12px', fontWeight: 600, width: '38%', verticalAlign: 'top' }}>{pretty(k)}</td>
                <td style={{ padding: '8px 12px', whiteSpace: 'pre-wrap' }}>{val(answers[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
